import type {Evaluator} from "@cm/graph-v3"
import {graphNode} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import type {InputNodeParameters} from "#template/fundamental/input/types"
import {inputNodeParameters} from "#template/fundamental/input/validation"
import {TemplateNode} from "#template/template-node/template-node"
import type {OnCompileContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import {fundamentalTypeList} from "#template/fundamental/registry"
import type {EvaluatedTemplateInput} from "#template/fundamental/evaluated-input"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluatedMetaData} from "#template/interface-descriptors"
import {NodesInfo} from "#template/interface-descriptors"
import {evaluateMetaData} from "#template/nodes/meta-data"
import {resolveTemplateInput} from "#template/fundamental/helpers"
import {visitNone} from "#template/template-node/visit-helpers"

const nodesInputDefinition: TemplateNodeDefinition<InputNodeParameters<TemplateNode[]>> = {
    nodeClass: "NodesInput",
    validation: {
        parameters: inputNodeParameters<TemplateNode[]>(z.array(graphNode(TemplateNode))),
        returns: getTemplateNodeReturns(),
    },
}

// Special-case input for array-of-nodes parameters. Doesn't follow the FT pattern (no namespaced
// templateOutputs, no info-class descriptor) — its evaluate dispatches each node through the FT
// registry to produce a flat list of EvaluatedTemplateInput.
@registerTemplateGraphNode
export class NodesInput extends TemplateNode<InputNodeParameters<TemplateNode[]>> implements EvaluableTemplateNode<EvaluatedTemplateInput[]> {
    public override get definition() {
        return nodesInputDefinition
    }

    static evaluateList(scope: GraphBuilderScope, evaluator: NodeEvaluator, nodes: readonly TemplateNode[]): BuilderInlet<EvaluatedTemplateInput[]> {
        return scope.merge(
            nodes.map<BuilderInlet<EvaluatedTemplateInput[]>>((value) => {
                for (const ft of fundamentalTypeList) {
                    const result = ft.makeEvaluatedInput(scope, evaluator, value, value)
                    if (result !== null) return scope.list([result])
                }
                if (value instanceof NodesInput) return value.evaluate(scope, evaluator)
                throw new Error(`Cannot use node ${value.nodeClass} in a node list`)
            }),
        )
    }

    private getTemplateInput(
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): BuilderInlet<{value: EvaluatedTemplateInput[]; origin: TemplateNode | undefined}> | undefined {
        return resolveTemplateInput<EvaluatedTemplateInput[]>(scope, evaluator, this.parameters.id, "nodes")
    }

    private evaluateImpl(
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): BuilderInlet<{value: EvaluatedTemplateInput[]; origin: TemplateNode | undefined}> {
        const templateInput = this.getTemplateInput(scope, evaluator)
        if (templateInput === undefined) {
            return scope.struct<{value: EvaluatedTemplateInput[]; origin: TemplateNode | undefined}>("NodesInputData", {
                value: NodesInput.evaluateList(scope, evaluator, this.parameters.default ?? []),
                origin: this,
            })
        }
        return templateInput
    }

    evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<EvaluatedTemplateInput[]> {
        const {templateScope} = evaluator
        return scope.pureLambda(
            scope.unresolvedToNull(templateScope.resolve<EvaluatedTemplateInput[]>(`templateInputs-${evaluator.getLocalId(this)}`)),
            (value) => value ?? [],
            "nullToEmptyArray",
        )
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<InputNodeParameters<TemplateNode[]>> {
        const {id, name, metaData} = this.parameters
        const {evaluator, currentTemplate} = context
        const {templateScope} = evaluator
        const scope = evaluator.getScope(this)
        const {descriptorList} = currentTemplate

        const data = this.evaluateImpl(scope, evaluator)
        const evaluatedMetaData = evaluateMetaData(scope, evaluator, metaData, id)

        descriptorList.push(
            scope.pureLambda(
                scope.tuple(id, name, data, evaluatedMetaData),
                ([id, name, {value, origin}, metaData]: [
                    string,
                    string,
                    {value: EvaluatedTemplateInput[]; origin: TemplateNode | undefined},
                    EvaluatedMetaData,
                ]) =>
                    new NodesInfo({
                        id,
                        name,
                        value,
                        type: "input",
                        origin,
                        metaData,
                    }),
                "nodesInfo",
            ),
        )

        templateScope.alias(scope.get(data, "value"), `templateInputs-${evaluator.getLocalId(this)}`)

        return visitNone(this.parameters)
    }
}
